import { Checkbox } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { BankAccountType } from '@portal/enum/accountType';
import { TaxIdentifierType } from '@portal/enum/onboardingType';
import useReduxState from '@portal/hooks/useReduxState';

import {
  PixActions,
  SalaryPortability as SalaryPortabilityActions,
  SettingsActions,
} from '~/actions';

import AdvancedButton from '~/components/AdvancedButton/AdvancedButton';
import MessageModal from '~/components/MessageModal/MessageModal';
import NewContactModal from '~/components/NewContactModal/NewContactModal';
import SalaryPortabilityFavored from '~/components/SalaryPortabilityFavored/SalaryPortabilityFavored';

import { getRouteStackPath } from '~/config/routes';
import { translate } from '~/services/i18n';
import * as MessageService from '~/services/message';
import { TransferType } from '@portal/enum/transfers';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { useNavigation } from '@portal/hooks/useNavigation';

const SalaryPortability = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();

  const {
    salaryPortability: { favored, error, success },
    settings: { settings },
    pix: { psp },
  } = useReduxState();

  const [alias, setAlias] = useState(favored?.alias ?? '');
  const [taxIdentifier, setTaxIdentifier] = useState(
    favored?.taxIdentifier ?? ''
  );
  const [bankUserName, setBankUserName] = useState(favored?.bankUserName ?? '');
  const [pspCode, setPspCode] = useState(favored?.psp);
  const [bankCode, setBankCode] = useState(favored?.bankCode ?? '');
  const [bankAgency, setBankAgency] = useState(favored?.bankAgency ?? '');
  const [bankAccount, setBankAccount] = useState(favored?.bankAccount ?? '');
  const [bankAccountDigit, setBankAccountDigit] = useState(
    favored?.bankAccountDigit ?? ''
  );
  const [accountType, setAccountType] = useState(BankAccountType.CURRENT);
  const [isNewContactModalVisible, setIsNewContactModalVisible] =
    useState(false);
  const [isMessageModalVisible, setIsMessageModalVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [portabilityType, setPortabilityType] = useState<'TED' | 'PIX'>('TED');
  const [pspList, setPspList] = useState<bank.Psp[]>(psp);

  const getDisabled = (): boolean => {
    if (alias.trim() === '') {
      return true;
    }
    if (taxIdentifier.trim() === '') {
      return true;
    }
    if (
      pspCode?.trim() === '' ||
      bankAccount.trim() === '' ||
      bankAccountDigit.trim() === '' ||
      bankAgency.trim() === ''
    ) {
      return true;
    }

    if (!confirmed) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    } else {
      setPortabilityType(settings.salaryPortabilityType);
    }
  }, [dispatch, settings]);

  useEffect(() => {
    setAlias(favored?.alias ?? '');
    setBankAccountDigit(favored?.bankAccountDigit ?? '');
    setBankAccount(favored?.bankAccount ?? '');
    setBankAgency(favored?.bankAgency ?? '');
    setBankCode(favored?.bankCode ?? '');
    setBankUserName(favored?.bankUserName ?? '');
    setAccountType(favored?.accountType ?? BankAccountType.CURRENT);
    setTaxIdentifier(favored?.taxIdentifier ?? '');
    setPspCode(favored?.psp ?? '');
  }, [favored]);

  useEffect(() => {
    dispatch(SalaryPortabilityActions.getSalaryPortabilityFavored());
    dispatch(PixActions.listPSPs());
  }, [dispatch]);

  useEffect(() => {
    if (!psp) {
      dispatch(PixActions.listPSPs());
    } else {
      setPspList(psp);
    }
  }, [dispatch, psp]);

  useEffect(() => {
    if (success) {
      dispatch(SalaryPortabilityActions.setSalaryPortabilitySuccess(null));
      setIsNewContactModalVisible(false);
      dispatch(SalaryPortabilityActions.getSalaryPortabilityFavored());
      if (success !== 'DELETED') {
        setIsSuccessModalVisible(true);

        return;
      }
      MessageService.success(
        `PAGES.MY_ACCOUNT.SALARY_PORTABILITY.SUCCESS_MESSAGES.DELETED`
      );
    }
  }, [dispatch, success]);

  useEffect(() => {
    if (error) {
      MessageService.error(`SALARY_PORTABILITY.${error.message}`, {
        originalError: error,
      });
      dispatch(SalaryPortabilityActions.setSalaryPortabilityError(null));
      navigate(getRouteStackPath('PROFILE', 'PROFILE_PAGE'));
    }
  }, [dispatch, error, navigate]);

  const handleRegisterFavored = (favored: models.Contact) => {
    if (favored.type === 'ted' || favored.type === 'pix') {
      dispatch(
        SalaryPortabilityActions.createSalaryPortabilityFavored(favored)
      );
    }
  };

  const handleUpdate = () => {
    dispatch(
      SalaryPortabilityActions.updateSalaryPortabilityFavored({
        alias,
        bankAccount,
        bankAccountDigit,
        bankAgency,
        personType: TaxIdentifierType.PF,
        accountType,
        bankCode,
        bankUserName,
        taxIdentifier,
        psp: pspCode,
      })
    );
  };

  const handleDelete = () => {
    dispatch(SalaryPortabilityActions.deleteSalaryPortabilityFavored());

    setIsMessageModalVisible(false);
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('PAGES.MY_ACCOUNT.TABS.ACCOUNT'),
            url: getRouteStackPath('PROFILE', 'PROFILE_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.MY_ACCOUNT.TABS.SALARY_PORTABILITY'),
          },
        ]}
      />
      <div className="salary-portability">
        <AdvancedPanelContentHeader
          title={translate('PAGES.MY_ACCOUNT.SALARY_PORTABILITY.TITLE')}
          description={translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.SUBTITLE'
          )}
          testId="salary-portability-header"
          goBackAction={() => navigate.back()}
        />
        {favored ? (
          <AdvancedCard testId="salary-portability-favored-card">
            <form>
              <SalaryPortabilityFavored
                alias={alias}
                bankAccount={bankAccount}
                setBankAccount={setBankAccount}
                bankAccountDigit={bankAccountDigit}
                setBankAccountDigit={setBankAccountDigit}
                bankAgency={bankAgency}
                setBankAgency={setBankAgency}
                setBankCode={setBankCode}
                bankCode={bankCode}
                setBankUserName={setBankUserName}
                accountType={accountType}
                setAccountType={setAccountType}
                taxIdentifier={taxIdentifier}
                bankCodeType={portabilityType === 'PIX' ? 'PSP' : 'CODE'}
                pspList={pspList}
                setPspCode={setPspCode}
                pspCode={pspCode}
              />
              <Checkbox
                className="mt-2"
                onChange={() => setConfirmed(!confirmed)}
                checked={confirmed}
              >
                {translate('PAGES.MY_ACCOUNT.SALARY_PORTABILITY.CONFIRM')}
              </Checkbox>
              <div className="salary-portability__buttons">
                <div className="salary-portability__buttons__button">
                  <AdvancedButton
                    text={translate(
                      'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.UPDATE'
                    )}
                    onClick={handleUpdate}
                    disabled={getDisabled()}
                  />
                </div>
                <AdvancedButton
                  text={translate('PAGES.MY_ACCOUNT.SALARY_PORTABILITY.DELETE')}
                  variant="outlined"
                  onClick={() => setIsMessageModalVisible(true)}
                />
              </div>
            </form>
          </AdvancedCard>
        ) : (
          <AdvancedCard testId="salary-portability-empty-card">
            <div className="salary-portability__empty">
              <ThemedText
                variant="h3"
                className="salary-portability__empty__message"
              >
                {translate(
                  'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.NO_FAVORED_ACCOUNT_REGISTERED.MESSAGE'
                )}
              </ThemedText>
              <AdvancedButton
                text={translate(
                  'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.NO_FAVORED_ACCOUNT_REGISTERED.BUTTON'
                )}
                onClick={() => setIsNewContactModalVisible(true)}
              />
            </div>
          </AdvancedCard>
        )}
        {isNewContactModalVisible ? (
          <NewContactModal
            title={translate(
              'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.CREATE_FAVORED_MODAL.TITLE'
            )}
            closeModal={() => setIsNewContactModalVisible(false)}
            onSubmit={handleRegisterFavored}
            type={
              portabilityType === 'PIX' ? TransferType.PIX : TransferType.TED
            }
            salaryPortabilityType={portabilityType}
            visible={isNewContactModalVisible}
            isSalaryPortabilityFavored
          />
        ) : null}
        <MessageModal
          message={translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.CONFIRM_DELETE.MESSAGE'
          )}
          onConfirm={handleDelete}
          setVisible={setIsMessageModalVisible}
          open={isMessageModalVisible}
          buttonOkText={translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.CONFIRM_DELETE.OK'
          )}
          buttonCancelText={translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.CONFIRM_DELETE.CANCEL'
          )}
          onCancel={() => setIsMessageModalVisible(false)}
        />
        <MessageModal
          message={`${translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.SUCCESS_MESSAGES.CREATED'
          )} ${translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.FAVORED.BANK'
          )} ${bankCode}-${bankUserName} ${translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.FAVORED.BANK_AGENCY'
          )} ${bankAgency} ${translate(
            'PAGES.MY_ACCOUNT.SALARY_PORTABILITY.FAVORED.BANK_ACCOUNT'
          )} ${bankAccount}-${bankAccountDigit}`}
          onConfirm={() => setIsSuccessModalVisible(false)}
          setVisible={setIsSuccessModalVisible}
          open={isSuccessModalVisible}
          buttonOkText={translate(
            'COMPONENTS.PIX_PORTABILITY_MODAL.CLAIM_AWAITING_RETURN_PSP_DONOR.BUTTON.OK'
          )}
        />
      </div>
    </>
  );
};

export default SalaryPortability;
